import React, { useMemo } from 'react';
import { OktaAuth } from '@okta/okta-auth-js';
import { OKTA_CONFIG } from './utils/okta';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import Authenticator from './feature/auth/Authenticator';
import '@elastic/eui/dist/eui_theme_light.css';
import { AuthContextProvider } from './components/context/AuthContext';
import { UserSettingsContextProvider } from './components/context/UserSettingsContext';
import { TimezoneContextProvider } from './components/context/TimezoneContext';
import { FeatureFlagsProvider, useFeatureFlags } from './components/context/FeatureFlagsContext';
import FullStoryInit from './components/FullStoryInitializer';
import WebUI from './components/redesign/WebUI';
import { ConfigContextProvider } from './components/context/ConfigContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1800000, // 30 min
    },
  },
});

interface AppContentProps {
  oktaAuth: OktaAuth;
}

const AppContent = ({ oktaAuth }: AppContentProps) => {
  const featureFlags = useFeatureFlags();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthContextProvider>
        <ConfigContextProvider>
          <UserSettingsContextProvider>
            <TimezoneContextProvider>
              <Authenticator oktaAuth={oktaAuth} oktaEnabled={featureFlags.oktaEnabled}>
                <WebUI />
                <FullStoryInit />
              </Authenticator>
            </TimezoneContextProvider>
          </UserSettingsContextProvider>
        </ConfigContextProvider>
      </AuthContextProvider>
    </QueryClientProvider>
  );
};

const App = () => {
  const oktaAuth = useMemo(() => {
    const auth = new OktaAuth(OKTA_CONFIG);
    auth.start();
    return auth;
  }, []);

  return (
    <FeatureFlagsProvider>
      <AppContent oktaAuth={oktaAuth} />
    </FeatureFlagsProvider>
  );
};

export default App;
